import { useState, useEffect, useMemo, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDateFR, WEEKDAYS_FR, WEEKDAYS_FULL, MONTHS_FULL } from "@/lib/dates";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays, Ban, Trash2, Phone, Users, Car, Plus } from "lucide-react";
import { toast } from "sonner";
import BookingDetailModal from "@/components/BookingDetailModal";

const statusMap = {
  confirmed: { label: "Confirmée", chip: "bg-[#FFD60A] text-[#050810]", dot: "#FFD60A" },
  done: { label: "Terminée", chip: "bg-[#00B4D8] text-[#050810]", dot: "#00B4D8" },
  cancelled: { label: "Annulée", chip: "bg-[#EF476F] text-white", dot: "#EF476F" },
};

const HOUR_PX = 46;

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(d.getDate() + n); return x; }
function startOfWeek(d) { return addDays(d, -((d.getDay() + 6) % 7)); }
function toMin(s) { const [a, b] = s.split(":").map(Number); return a * 60 + b; }

export default function PlanningCalendar() {
  const [view, setView] = useState("week"); // day | week | month
  const [anchor, setAnchor] = useState(() => new Date());
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ start_date: "", start_time: "09:00", end_date: "", end_time: "18:00", reason: "", allDay: false });
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const range = useMemo(() => {
    if (view === "day") return { from: iso(anchor), to: iso(anchor) };
    if (view === "week") { const s = startOfWeek(anchor); return { from: iso(s), to: iso(addDays(s, 6)) }; }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return { from: iso(gridStart), to: iso(addDays(gridStart, 41)) };
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rb, rk] = await Promise.all([
        api.get("/admin/bookings", { params: { from_date: range.from, to_date: range.to } }),
        api.get("/admin/blocks", { params: { from_date: range.from, to_date: range.to } }),
      ]);
      setBookings(rb.data.filter((b) => b.status !== "cancelled"));
      setBlocks(rk.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (adding) { const d = iso(anchor); setForm((f) => ({ ...f, start_date: f.start_date || d, end_date: f.end_date || d })); }
  }, [adding, anchor]);

  const shift = (n) => {
    if (view === "day") setAnchor((a) => addDays(a, n));
    else if (view === "week") setAnchor((a) => addDays(a, n * 7));
    else setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + n, 1));
  };

  const [minHour, maxHour] = useMemo(() => {
    let lo = 24, hi = 0;
    const wh = settings?.weekly_hours || {};
    Object.values(wh).forEach((c) => {
      if (c?.open) { lo = Math.min(lo, Math.floor(toMin(c.start) / 60)); hi = Math.max(hi, Math.ceil(toMin(c.end) / 60)); }
    });
    if (lo >= hi) return [8, 19];
    return [lo, hi];
  }, [settings]);

  const addBlock = async () => {
    const start_date = form.start_date || iso(anchor);
    const end_date = form.end_date || start_date;
    try {
      await api.post("/admin/blocks", {
        start_date,
        start_time: form.allDay ? "00:00" : form.start_time,
        end_date,
        end_time: form.allDay ? "23:59" : form.end_time,
        reason: form.reason,
      });
      toast.success("Indisponibilité ajoutée");
      setForm({ start_date: "", start_time: "09:00", end_date: "", end_time: "18:00", reason: "", allDay: false });
      setAdding(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delBlock = async (id) => {
    try { await api.delete(`/admin/blocks/${id}`); toast.success("Indisponibilité retirée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const title = useMemo(() => {
    if (view === "day") return formatDateFR(iso(anchor));
    if (view === "week") { const s = startOfWeek(anchor), e = addDays(s, 6); return `${s.getDate()} ${MONTHS_FULL[s.getMonth()]} – ${e.getDate()} ${MONTHS_FULL[e.getMonth()]} ${e.getFullYear()}`; }
    return `${MONTHS_FULL[anchor.getMonth()]} ${anchor.getFullYear()}`;
  }, [view, anchor]);

  const byDate = (dstr) => bookings.filter((b) => b.date === dstr).sort((a, b) => a.start_time.localeCompare(b.start_time));
  const blocksByDate = (dstr) => blocks.map((nb) => {
    const sd = nb.start_date || nb.date; const ed = nb.end_date || nb.date;
    if (!sd || dstr < sd || dstr > ed) return null;
    return { ...nb, start_time: dstr === sd ? (nb.start_time || "00:00") : "00:00", end_time: dstr === ed ? (nb.end_time || "23:59") : "23:59" };
  }).filter(Boolean);

  return (
    <div data-testid="planning-calendar">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="p-2 border border-[#1A2642] rounded-full hover:border-[#00B4D8] transition"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => setAnchor(new Date())} data-testid="cal-today" className="text-xs font-bold border border-[#1A2642] rounded-full px-3 py-2 hover:border-[#00B4D8] transition">Aujourd'hui</button>
          <button onClick={() => shift(1)} data-testid="cal-next" className="p-2 border border-[#1A2642] rounded-full hover:border-[#00B4D8] transition"><ChevronRight className="w-5 h-5" /></button>
          <h2 className="font-heading text-base sm:text-lg font-bold capitalize ml-1 sm:ml-2">{title}</h2>
        </div>
        <div className="flex gap-1 p-1 bg-[#050810] border border-[#1A2642] rounded-full self-start">
          {[["day", "Jour"], ["week", "Semaine"], ["month", "Mois"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
              className={`px-3 sm:px-4 py-1.5 rounded-full text-sm font-semibold transition ${view === k ? "bg-[#00B4D8] text-[#050810]" : "text-[#94A3B8] hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Block creator — available in all views */}
      <div className="mb-5">
        {!adding ? (
          <button onClick={() => setAdding(true)} data-testid="add-block-btn" className="flex items-center gap-2 border border-[#EF476F]/40 text-[#EF476F] font-semibold text-sm rounded-full px-4 py-2.5 hover:bg-[#EF476F]/10 transition"><Ban className="w-4 h-4" /> Bloquer un créneau</button>
        ) : (
          <BlockForm form={form} setForm={setForm} onAddBlock={addBlock} onCancel={() => setAdding(false)} />
        )}
      </div>

      {loading ? <Loader2 className="w-7 h-7 animate-spin text-[#00B4D8] mx-auto my-12" /> : (
        <>
          {view === "month" && <MonthView anchor={anchor} byDate={byDate} blocksByDate={blocksByDate} onPickDay={(d) => { setAnchor(d); setView("day"); }} />}
          {view === "week" && <WeekView anchor={anchor} bookings={bookings} blocksForDate={blocksByDate} minHour={minHour} maxHour={maxHour} onPickDay={(d) => { setAnchor(d); setView("day"); }} onSelect={setSelected} />}
          {view === "day" && <DayView items={byDate(iso(anchor))} blocks={blocksByDate(iso(anchor))} onDelBlock={delBlock} onSelect={setSelected} />}
        </>
      )}

      {selected && <BookingDetailModal booking={selected} onClose={() => setSelected(null)} onChanged={() => load()} />}
    </div>
  );
}

function MonthView({ anchor, byDate, blocksByDate, onPickDay }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const todayIso = iso(new Date());
  return (
    <div data-testid="month-view" className="border border-[#1A2642] rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 bg-[#0C1322] border-b border-[#1A2642]">
        {WEEKDAYS_FR.map((d) => <div key={d} className="px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const dstr = iso(d);
          const inMonth = d.getMonth() === anchor.getMonth();
          const items = byDate(dstr);
          const blks = blocksByDate(dstr);
          const isToday = dstr === todayIso;
          return (
            <button key={dstr} onClick={() => onPickDay(d)} data-testid={`month-day-${dstr}`}
              className={`min-h-[92px] text-left p-1.5 border-b border-r border-[#1A2642] transition hover:bg-[#00B4D8]/5 ${inMonth ? "" : "opacity-35"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-[#00B4D8] text-[#050810]" : "text-white"}`}>{d.getDate()}</span>
                {items.length > 0 && <span className="text-[10px] font-bold text-[#FFD60A]">{items.length} RDV</span>}
              </div>
              <div className="space-y-0.5">
                {items.slice(0, 2).map((b) => (
                  <div key={b.id} className={`text-[10px] truncate rounded px-1 py-0.5 ${statusMap[b.status].chip}`}>{b.start_time} {b.service_name}</div>
                ))}
                {items.length > 2 && <div className="text-[10px] text-[#94A3B8]">+{items.length - 2} autre(s)</div>}
                {blks.length > 0 && <div className="text-[10px] truncate rounded px-1 py-0.5 bg-[#EF476F]/20 text-[#EF476F]">Indispo</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ anchor, bookings, blocksForDate, minHour, maxHour, onPickDay, onSelect }) {
  const s = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(s, i));
  const hours = Array.from({ length: maxHour - minHour }, (_, i) => minHour + i);
  const bodyH = (maxHour - minHour) * HOUR_PX;
  const todayIso = iso(new Date());
  const posY = (m) => ((m - minHour * 60) / 60) * HOUR_PX;

  return (
    <div data-testid="week-view" className="border border-[#1A2642] rounded-lg overflow-x-auto">
      <div className="min-w-[760px]">
        {/* header */}
        <div className="grid bg-[#0C1322] border-b border-[#1A2642]" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
          <div />
          {days.map((d) => {
            const isToday = iso(d) === todayIso;
            return (
              <button key={iso(d)} onClick={() => onPickDay(d)} className="px-1 py-2 text-center border-l border-[#1A2642] hover:bg-[#00B4D8]/5 transition">
                <div className="text-[11px] font-bold uppercase text-[#94A3B8]">{WEEKDAYS_FR[(d.getDay() + 6) % 7]}</div>
                <div className={`text-lg font-bold mx-auto w-8 h-8 flex items-center justify-center rounded-full ${isToday ? "bg-[#00B4D8] text-[#050810]" : "text-white"}`}>{d.getDate()}</div>
              </button>
            );
          })}
        </div>
        {/* body */}
        <div className="grid relative" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
          {/* hour gutter */}
          <div className="relative" style={{ height: bodyH }}>
            {hours.map((h, i) => (
              <div key={h} className="absolute right-1.5 text-[10px] text-[#475569] -translate-y-1/2" style={{ top: i * HOUR_PX }}>{String(h).padStart(2, "0")}:00</div>
            ))}
          </div>
          {days.map((d) => {
            const dstr = iso(d);
            const dayBookings = bookings.filter((b) => b.date === dstr);
            const dayBlocks = blocksForDate(dstr);
            return (
              <div key={dstr} className="relative border-l border-[#1A2642]" style={{ height: bodyH }}>
                {hours.map((h, i) => <div key={h} className="absolute left-0 right-0 border-t border-[#1A2642]/50" style={{ top: i * HOUR_PX }} />)}
                {dayBlocks.map((b) => {
                  const top = posY(toMin(b.start_time)); const height = Math.max(posY(toMin(b.end_time)) - top, 16);
                  return <div key={b.id} className="absolute left-0.5 right-0.5 rounded bg-[#EF476F]/15 border border-[#EF476F]/40 px-1 py-0.5 overflow-hidden" style={{ top, height }} title={b.reason || "Indisponible"}>
                    <span className="text-[9px] font-bold text-[#EF476F] flex items-center gap-0.5"><Ban className="w-2.5 h-2.5" />{b.reason || "Indispo"}</span>
                  </div>;
                })}
                {dayBookings.map((b) => {
                  const top = posY(toMin(b.start_time)); const height = Math.max(posY(toMin(b.end_time)) - top, 18);
                  const sc = statusMap[b.status];
                  return <div key={b.id} onClick={() => onSelect(b)} data-testid={`week-booking-${b.id}`} className={`absolute left-0.5 right-0.5 rounded px-1.5 py-0.5 overflow-hidden shadow cursor-pointer hover:ring-2 hover:ring-white/50 transition ${sc.chip}`} style={{ top, height }} title={`${b.start_time}-${b.end_time} · ${b.service_name} · ${b.customer_name}`}>
                    <div className="text-[10px] font-bold leading-tight truncate">{b.start_time} {b.service_name}</div>
                    <div className="text-[9px] leading-tight truncate opacity-80">{b.customer_name}</div>
                  </div>;
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BlockForm({ form, setForm, onAddBlock, onCancel }) {
  const inputCls = "bg-[#050810] border border-[#1A2642] text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]";
  return (
    <div className="bg-[#0C1322] border border-[#EF476F]/40 rounded-lg p-4" data-testid="block-form">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#EF476F] mb-3">Indisponibilité / congés</p>
      <label className="flex items-center gap-2 text-sm text-white cursor-pointer mb-3">
        <input type="checkbox" className="w-4 h-4 accent-[#EF476F]" checked={form.allDay} data-testid="block-allday" onChange={(e) => setForm({ ...form, allDay: e.target.checked })} /> Journée(s) entière(s)
      </label>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Début</label>
          <div className="flex items-center gap-2">
            <input type="date" className={`${inputCls} flex-1 min-w-0`} data-testid="block-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            {!form.allDay && <input type="time" className={`${inputCls} w-24`} data-testid="block-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />}
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Fin</label>
          <div className="flex items-center gap-2">
            <input type="date" className={`${inputCls} flex-1 min-w-0`} data-testid="block-end-date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            {!form.allDay && <input type="time" className={`${inputCls} w-24`} data-testid="block-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />}
          </div>
        </div>
      </div>
      <input className={`${inputCls} w-full mb-3`} data-testid="block-reason" placeholder="Motif (congés, pause, rendez-vous…)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
      <div className="flex gap-2">
        <button onClick={onAddBlock} data-testid="save-block-btn" className="flex items-center gap-1.5 bg-[#EF476F] text-white font-bold text-sm rounded-full px-5 py-2.5 hover:bg-[#d63a5f] transition"><Ban className="w-4 h-4" /> Bloquer</button>
        <button onClick={onCancel} className="border border-[#1A2642] rounded-full px-5 py-2.5 text-sm hover:border-white transition">Annuler</button>
      </div>
    </div>
  );
}

function DayView({ items, blocks, onDelBlock, onSelect }) {
  const merged = [
    ...items.map((b) => ({ kind: "booking", sort: b.start_time, ...b })),
    ...blocks.map((b) => ({ kind: "block", sort: b.start_time, ...b })),
  ].sort((a, b) => a.sort.localeCompare(b.sort));

  return (
    <div data-testid="day-view">
      {merged.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#1A2642] rounded-lg">
          <CalendarDays className="w-10 h-10 text-[#475569] mx-auto mb-3" />
          <p className="text-[#94A3B8]">Aucun rendez-vous ni indisponibilité ce jour.</p>
        </div>
      ) : (
        <div className="relative pl-4">
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#1A2642]" />
          <div className="space-y-3">
            {merged.map((it) => it.kind === "block" ? (
              <div key={it.id} className="relative" data-testid={`planning-block-${it.id}`}>
                <div className="absolute -left-[13px] top-4 w-3.5 h-3.5 rounded-full border-2 bg-[#050810] border-[#EF476F]" />
                <div className="ml-5 bg-[#EF476F]/8 border border-[#EF476F]/30 rounded-lg p-4 flex items-center gap-3">
                  <Ban className="w-5 h-5 text-[#EF476F] shrink-0" />
                  <div className="font-heading text-lg font-black text-[#EF476F] w-28 shrink-0">{it.start_time}<span className="text-[#EF476F]/60 text-sm font-normal"> → {it.end_time}</span></div>
                  <div className="flex-1 min-w-0"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded border border-[#EF476F]/30 text-[#EF476F]">Indisponible</span>{it.reason && <p className="text-sm text-[#94A3B8] mt-1">{it.reason}</p>}</div>
                  <button onClick={() => onDelBlock(it.id)} data-testid={`del-block-${it.id}`} className="p-2 border border-[#EF476F]/30 text-[#EF476F] rounded-full hover:bg-[#EF476F]/10 transition shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <div key={it.id} className="relative" data-testid={`planning-item-${it.id}`}>
                <div className="absolute -left-[13px] top-4 w-3.5 h-3.5 rounded-full border-2 bg-[#050810]" style={{ borderColor: statusMap[it.status].dot }} />
                <div onClick={() => onSelect(it)} className="ml-5 bg-[#0C1322] border border-[#1A2642] rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer hover:border-[#00B4D8] transition">
                  <div className="font-heading text-lg font-black text-[#FFD60A] w-28 shrink-0">{it.start_time}<span className="text-[#475569] text-sm font-normal"> → {it.end_time}</span></div>
                  <div className="flex-1 min-w-0">
                    <span className="font-bold">{it.service_name}</span>
                    {it.amount_paid != null && it.amount_paid > 0 && <span className="ml-2 text-[11px] font-bold text-[#06D6A0]">· Payé {it.amount_paid} €</span>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-[#94A3B8]">
                      <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />{it.customer_name}</span>
                      <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" />{it.customer_phone}</span>
                      <span className="flex items-center gap-1.5"><Car className="w-4 h-4" />{it.vehicle_marque} {it.vehicle_modele} ({it.vehicle_annee})</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
